import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useSelector } from 'react-redux';
import './DestinationInput.css';

// Component for selecting travel destinations with hierarchical selection (country > region > resort)
function DestinationInput({ value, onChange }) {
  // Get destinations data from Redux store
  const destinations = useSelector((state) => state.search.destinations);

  // Create hierarchical options for countries, regions, and resorts
  const options = React.useMemo(() => {
    // Create country-level options (highest level)
    const countries = [...new Set(destinations.map(d => d.country))].map(country => ({
      country,
      region: 'Any region',
      resort: 'Any resort',
      code: `any-any-${country}`.toLowerCase(),
      type: 'country'
    }));

    // Create region-level options (mid level)
    const regions = [...new Set(destinations.map(d => `${d.region}-${d.country}`))].map(regionCountry => {
      const [region, country] = regionCountry.split('-');
      return {
        country,
        region,
        resort: 'Any resort',
        code: `any-${region}-${country}`.toLowerCase(),
        type: 'region'
      };
    });

    // Combine all levels: specific resorts, regions, and countries
    return [...destinations, ...regions, ...countries];
  }, [destinations]);

  // Find the matching destination object from the current value
  const getCurrentValue = () => {
    if (!value || !value.destination) return null;
    
    return options.find(dest => {
      if (dest.type === 'country') {
        return dest.country === value.destination;
      }
      if (dest.type === 'region') {
        return `${dest.region}, ${dest.country}` === value.destination;
      }
      return `${dest.resort}, ${dest.region}, ${dest.country}` === value.destination;
    }) || null;
  };

  // Calculate priority for search matches to determine display order
  const getMatchPriority = (option, searchTerm) => {
    if (!option) return 5;
    const searchLower = searchTerm.toLowerCase();
    const resort = option.resort?.toLowerCase() || '';
    const region = option.region?.toLowerCase() || '';
    const country = option.country?.toLowerCase() || '';

    // Priority order: country > region > specific resort
    if (country.includes(searchLower) && option.type === 'country') return 1;
    if (region.includes(searchLower) && option.type === 'region') return 2;
    if (resort.includes(searchLower) && resort !== 'Any resort') return 3;
    if (region.includes(searchLower) && region !== 'Any region') return 4;
    if (country.includes(searchLower)) return 5;
    
    return 6;
  };

  // Filter and sort options based on user input
  const filterOptions = (options, { inputValue }) => {
    if (!inputValue) {
      // When no input, show hierarchical order: countries > regions > resorts
      return options.sort((a, b) => {
        if (a.type === 'country' && b.type !== 'country') return -1;
        if (a.type !== 'country' && b.type === 'country') return 1;
        if (a.type === 'region' && b.type !== 'region') return -1;
        if (a.type !== 'region' && b.type === 'region') return 1;
        return (a.resort || '').localeCompare(b.resort || '');
      });
    }

    // Filter options based on search term and sort by relevance
    return options
      .filter(option => {
        const searchTerm = inputValue.toLowerCase();
        return (
          (option.resort?.toLowerCase() || '').includes(searchTerm) ||
          (option.region?.toLowerCase() || '').includes(searchTerm) ||
          (option.country?.toLowerCase() || '').includes(searchTerm)
        );
      })
      .sort((a, b) => {
        const priorityA = getMatchPriority(a, inputValue);
        const priorityB = getMatchPriority(b, inputValue);
        if (priorityA !== priorityB) return priorityA - priorityB;
        
        // Secondary sort by type and name when priorities match
        if (a.type === b.type) {
          return (a.resort || a.region || a.country)
            .localeCompare(b.resort || b.region || b.country);
        }
        
        // Maintain hierarchical ordering within same priority
        if (a.type === 'country') return -1;
        if (b.type === 'country') return 1;
        if (a.type === 'region') return -1;
        if (b.type === 'region') return 1;
        return 0;
      });
  };

  return (
    <Autocomplete
      value={getCurrentValue()}
      onChange={(event, newValue) => {
        if (!newValue) {
          // Clear selection
          onChange({
            destination: '',
            destinationTuple: ['', '', ''], // [country, region, resort]
          });
        } else {
          // Format display string based on selection type
          const displayString = newValue.type === 'country' 
            ? newValue.country
            : newValue.type === 'region'
              ? `${newValue.region}, ${newValue.country}`
              : `${newValue.resort}, ${newValue.region}, ${newValue.country}`;

          // Create tuple based on selection type
          const destinationTuple = newValue.type === 'country' 
            ? [newValue.country, '', '']  // country only
            : newValue.type === 'region'
              ? [newValue.country, newValue.region, '']  // country and region
              : [newValue.country, newValue.region, newValue.resort];  // all values

          onChange({
            destination: displayString,
            destinationTuple
          });
        }
      }}
      options={options}
      filterOptions={filterOptions}
      getOptionLabel={(option) => {
        if (!option) return '';
        // Format display label based on option type
        if (option.type === 'country') {
          return option.country;
        }
        if (option.type === 'region') {
          return `${option.region}, ${option.country}`;
        }
        return `${option.resort}, ${option.region}, ${option.country}`;
      }}
      // Custom render function for dropdown options
      renderOption={(props, option) => {
        const { key, ...otherProps } = props;
        return (
          <li key={key} {...otherProps}>
            <div>
              {option.type === 'country' ? (
                <span>{option.country}</span>
              ) : option.type === 'region' ? (
                <>
                  <span>{option.region}</span>
                  <br />
                  <small style={{ color: 'var(--text-gray)' }}>
                    {option.country}
                  </small>
                </>
              ) : (
                <>
                  <span>{option.resort}</span>
                  <br />
                  <small style={{ color: 'var(--text-gray)' }}>
                    {option.region}, {option.country}
                  </small>
                </>
              )}
            </div>
          </li>
        );
      }}
      // Custom render function for input field
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Where do you want to go?"
          fullWidth
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <span className="material-icons">search</span>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{
        '& .MuiAutocomplete-clearIndicator': {
          display: 'none'
        }
      }}
    />
  );
}

export default DestinationInput;